export type SubscriptionTier = 'free' | 'student' | 'power' | 'plus' | 'pro';

export function normalizeSubscriptionTier(value: unknown): SubscriptionTier {
  // Recognize the later public name using the restored Student application limits.
  if (value === 'unlimited') return 'student';
  return value === 'student' || value === 'power' || value === 'plus' || value === 'pro'
    ? value
    : 'free';
}

export interface TierLimits {
  generationsPerMonth: number; // Infinity for legacy pro
  maxLibraryDecks: number; // No saved-deck count cap on any plan.
  depthOptions: ('concise' | 'balanced' | 'thorough')[];
  originalFileStorage: boolean;
  priorityQueue: boolean;
}

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  free: {
    generationsPerMonth: 3,
    maxLibraryDecks: Infinity,
    depthOptions: ['concise'],
    originalFileStorage: false,
    priorityQueue: false,
  },
  student: {
    generationsPerMonth: 40,
    maxLibraryDecks: Infinity,
    depthOptions: ['concise', 'balanced', 'thorough'],
    originalFileStorage: true,
    priorityQueue: true,
  },
  power: {
    generationsPerMonth: 75,
    maxLibraryDecks: Infinity,
    depthOptions: ['concise', 'balanced', 'thorough'],
    originalFileStorage: true,
    priorityQueue: true,
  },
  // Legacy tiers remain valid so existing subscribers keep their benefits.
  plus: {
    generationsPerMonth: 100,
    maxLibraryDecks: Infinity,
    depthOptions: ['concise', 'balanced', 'thorough'],
    originalFileStorage: true,
    priorityQueue: true,
  },
  pro: {
    generationsPerMonth: Infinity,
    maxLibraryDecks: Infinity,
    depthOptions: ['concise', 'balanced', 'thorough'],
    originalFileStorage: true,
    priorityQueue: true,
  },
};

export interface SubscriptionState {
  tier: SubscriptionTier;
  limits: TierLimits;
  usage: { generationsUsed: number };
  deckCount: number;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | null;
  cancelAtPeriodEnd: boolean;
  loading: boolean;
  bonusGenerations: number;
  // Derived helpers
  canGenerate: boolean;
  canSaveToLibrary: boolean;
  generationsRemaining: number; // Infinity for legacy pro
}

// Serializable account data for the initial server render. Derived limits stay
// in the provider so values such as Infinity never need JSON serialization.
export interface SubscriptionSnapshot {
  userId: string | null;
  tier: SubscriptionTier;
  generationsUsed: number;
  deckCount: number;
  bonusGenerations: number;
  status: SubscriptionState['status'];
  cancelAtPeriodEnd: boolean;
}

export function deriveSubscriptionState(
  tier: SubscriptionTier,
  generationsUsed: number,
  deckCount: number,
  status: SubscriptionState['status'],
  cancelAtPeriodEnd: boolean,
  loading: boolean,
  bonusGenerations = 0,
): SubscriptionState {
  // Downgrade to free limits if payment is past due
  const effectiveTier = (tier !== 'free' && status === 'past_due') ? 'free' : tier;
  const limits = TIER_LIMITS[effectiveTier];
  const baseRemaining = limits.generationsPerMonth === Infinity
    ? Infinity
    : Math.max(0, limits.generationsPerMonth - generationsUsed);
  const generationsRemaining = baseRemaining === Infinity
    ? Infinity
    : baseRemaining + Math.max(0, bonusGenerations);

  return {
    tier, // Keep the real tier for display (account page shows "Plus — past due")
    limits,
    usage: { generationsUsed },
    deckCount,
    status,
    cancelAtPeriodEnd,
    loading,
    bonusGenerations: Math.max(0, bonusGenerations),
    canGenerate: generationsRemaining > 0,
    canSaveToLibrary: deckCount < limits.maxLibraryDecks,
    generationsRemaining,
  };
}
